import React, { useCallback, useEffect, useMemo } from 'react';
import { set, get, cloneDeep, isBoolean } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useProductCostingState } from '../ProductCostingContext';
import { shortenStringWithTooltip, roundOff, restrictToTwoDecimals, checkIsNumber } from '../../../../Components/helper';
import useDetailedPageHook from '../Hooks/useDetailedPageHook';
import useShowHideSectionHooks from '../Hooks/useShowHideSectionHooks';
import useValidationHooks from '../Hooks/useValidationHooks';
import { quantitiesFieldpath, errorpath } from '../fieldPath';
import BombiixAGGrid, { HierExpandCell, ExpandChevronCell } from '../../../../Components/BombiixAGGrid';
import type { BombiixColumnDefOrGroup, BombiixFillBatchItem, BombiixRowId } from '../../../../Components/BombiixAGGrid/types';
import CostingCardShell from './CostingCardShell';
import type { DetailedQuantitiesState, QuantitiesRow } from '../types';
import { useLengthLabel } from '../../../../hooks/useLengthLabel';


const CORE_COLLAPSED_HEIGHT = '352px';

/** The redux slice this table reads. */
interface QuantitiesRootState {
  newProductCostingData: { productSizes?: string[] };
}

/**
 * One rendered row. Declared with `type` rather than `interface` on purpose: BombiixAGGrid
 * constrains its row generic to `Record<string, unknown> & {…}`, and TypeScript only grants the
 * implicit string index signature to type aliases.
 */
type QuantitiesGridRow = {
  __cagId: string;
  __rowType: 'summary' | 'data' | 'grandTotal';
  parentIndex?: number | undefined;
  chindex?: number | undefined;
  __showChevron?: boolean | undefined;
  isExpanded?: boolean | undefined;
  colourway?: React.ReactNode | undefined;
  size?: React.ReactNode | undefined;
  length?: React.ReactNode | undefined;
  quantity?: unknown;
  colourwayId?: string | undefined;
  readonlyQty?: boolean | undefined;
  __err?: string | undefined;
};

interface ExpandAllHeaderProps {
  allExpanded: boolean;
  onToggleAll: (next: boolean) => void;
}

/**
 * Expand/collapse-all chevron for this table's header.
 *
 * BombiixAGGrid ships its own ExpandAllHeader, but that one drives AG Grid master/detail nodes.
 * This table fakes its hierarchy with flat rows and synthetic markers, so expansion is owned by the
 * caller and the header has to be too. It renders the shared ExpandChevronCell, so the header glyph
 * uses the same classes.
 */
function ExpandAllHeader(props: ExpandAllHeaderProps): React.JSX.Element {
  const { allExpanded, onToggleAll } = props;
  return (
    <div className="cag-header cag-header--expand-all">
      <ExpandChevronCell
        expanded={Boolean(allExpanded)}
        onToggle={() => onToggleAll(!allExpanded)}
        expandLabel="Expand all"
        collapseLabel="Collapse all"
      />
    </div>
  );
}

export function QuantitiesTable(): React.JSX.Element {
  const {
    productCostingstate,
    detailedQuanitiesState,
    colSizeLengthToggleState,
    setDetailedQuanitiesState,
    isAllExpandedState,
    setExpandAllState,
    globalError,
    CoastingAddEditRights,
  } = useProductCostingState();
  const { productid } = useParams<{ productid?: string }>();
  const { isBreakdown, isGlobalUrl } = productCostingstate;
  const { colourWayToggle, sizeLengthToggle } = colSizeLengthToggleState;
  const {
    newProductCostingData: { productSizes },
  } = useSelector((state: QuantitiesRootState) => state);

  const {
    quantities: { quantitiesInfo, overAllSumOfQuantityPerSizCol, totLength, totSize, totColourways },
  } = detailedQuanitiesState;

  const { quantitesAllExpand } = isAllExpandedState;
  const { quantities } = globalError;

  const { hideLengthCol, hideColourCol, onLoadDisable, hideSizeCol, isDisabled } = useShowHideSectionHooks();
  const { t } = useTranslation('common');
  const lengthDisplayLabel = useLengthLabel(t('DEVLOPMENT_ORDER.EXPAND_VIEW.QUANTITIES_TABLE.LENGTH'));

  const { handleQuantities, calculateQuantities } = useDetailedPageHook();
  /* Called with no arguments, exactly as the original does: this table only needs
     `onChangeCheckIfErrorExist` and never invokes `checkValidation`. */
  const { onChangeCheckIfErrorExist } = useValidationHooks();

  useEffect(() => {
    if (!onLoadDisable && !productid && !isGlobalUrl) {
      handleQuantities(colourWayToggle, sizeLengthToggle);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onLoadDisable, productid, isGlobalUrl]);

  const expandQuantitiesChild = (index: number | null, expandAll?: boolean): void => {
    setDetailedQuanitiesState((state) => {
      const copyState: DetailedQuantitiesState = cloneDeep(state);
      if (isBoolean(expandAll)) {
        copyState.quantities.quantitiesInfo = copyState?.quantities?.quantitiesInfo?.map((val) => ({ ...val, isExpanded: expandAll }));
      } else if ((index ?? -1) >= 0) {
        const path = `quantities.quantitiesInfo[${index}].isExpanded`;
        set(copyState, path, !get(copyState, path, false));
      }
      return { ...state, ...copyState };
    });
    if (isBoolean(expandAll)) {
      setExpandAllState((prev) => ({ ...prev, quantitesAllExpand: expandAll as boolean }));
    } else if (quantitesAllExpand) {
      setExpandAllState((prev) => ({ ...prev, quantitesAllExpand: false }));
    }
  };

  const handleQtyChange = async (value: unknown, path: string, colourwayId?: string): Promise<void> => {
    const copyState: DetailedQuantitiesState = cloneDeep(detailedQuanitiesState);
    set(copyState, path, value);
    await calculateQuantities(copyState, colourwayId);
  };

  const updateError = (path: string): void => onChangeCheckIfErrorExist(path, globalError);

  const shouldShowExpand = hideColourCol || hideLengthCol || hideSizeCol;
  const showSizeCol = (!colourWayToggle && !sizeLengthToggle) || hideSizeCol;

  const rowData = useMemo<QuantitiesGridRow[]>(() => {
    const rows: QuantitiesGridRow[] = [];
    const list: QuantitiesRow[] = Array.isArray(quantitiesInfo) ? quantitiesInfo : [];

    list.forEach((data, index) => {
      const isCollapsed = !data?.isExpanded && data?.expandData?.length !== 1 && !isBreakdown;

      if (isCollapsed) {
        let sizeDisp: React.ReactNode = '';
        if (!colourWayToggle && !sizeLengthToggle && !hideSizeCol) sizeDisp = productSizes?.join(', ');
        else if (colourWayToggle) sizeDisp = Array.isArray(totSize) ? shortenStringWithTooltip(totSize.join(', '), 15) : '';
        else sizeDisp = (data?.sizeRange as Array<{ name?: string }> | undefined)?.[0]?.name || '';

        rows.push({
          __cagId: `p${index}-sum`,
          __rowType: 'summary',
          parentIndex: index,
          __showChevron: true,
          isExpanded: false,
          colourway: (data?.colourway as { name?: string } | undefined)?.name,
          size: sizeDisp,
          length: Array.isArray(totLength) ? shortenStringWithTooltip(totLength.join(', '), 25) : '',
          quantity: roundOff(data?.sumOfQuantityPerSizCol, 2, true),
        });
      } else {
        const expandArr = !isBreakdown ? data?.expandData : [data?.expandData?.[0]];
        (expandArr || []).forEach((element, chindex) => {
          let sizeDisp: React.ReactNode = '';
          if (!colourWayToggle && !sizeLengthToggle) sizeDisp = productSizes?.join(', ');
          else if (hideSizeCol) sizeDisp = (element?.size as string | undefined) || '';

          rows.push({
            __cagId: `p${index}-c${chindex}`,
            __rowType: 'data',
            parentIndex: index,
            chindex,
            __showChevron: chindex === 0 && data?.expandData?.length !== 1 && !isBreakdown,
            isExpanded: data?.isExpanded as boolean | undefined,
            colourway: (data?.colourway as { name?: string } | undefined)?.name,
            size: sizeDisp,
            length: (element?.length as { name?: string } | undefined)?.name || '',
            quantity: element?.quantityPerSizCol,
            colourwayId: (element?.colourway as { _id?: string } | undefined)?._id,
            readonlyQty: isDisabled || !CoastingAddEditRights,
            __err: get(quantities, `[${index}][${chindex}].quantityPerSizCol`, ''),
          });
        });
      }
    });

    if (list.length > 1) {
      const colourwayLabel = (totColourways?.length ?? 0) > 1 ? `${totColourways.length} Colourway(s)` : `${totColourways?.length || 0} Colourway`;
      const sizeLabel = (totSize?.length ?? 0) > 1 ? `${totSize.length} Size(s)` : `${totSize?.length || 0} Size`;
      const lengthLabel =
        (totLength?.length ?? 0) > 1 ? `${totLength.length} ${lengthDisplayLabel}(s)` : `${totLength?.length || 0} ${lengthDisplayLabel}`;
      rows.push({
        __cagId: 'overall-total',
        __rowType: 'grandTotal',
        colourway: hideColourCol ? colourwayLabel : '',
        size: hideSizeCol ? sizeLabel : '',
        length: hideLengthCol ? lengthLabel : '',
        quantity: `${roundOff(overAllSumOfQuantityPerSizCol, 2, true)} Units`,
      });
    }
    return rows;
  }, [
    quantitiesInfo,
    isBreakdown,
    colourWayToggle,
    sizeLengthToggle,
    hideSizeCol,
    hideColourCol,
    hideLengthCol,
    productSizes,
    totSize,
    totLength,
    totColourways,
    overAllSumOfQuantityPerSizCol,
    quantities,
    isDisabled,
    CoastingAddEditRights,
    lengthDisplayLabel,
  ]);

  const onRowChange = useCallback(
    (rowId: BombiixRowId, field: string, value: unknown) => {
      const row = rowData.find((item) => item.__cagId === rowId);
      if (!row || row.__rowType !== 'data') return;
      if (field === 'quantity') {
        const restricted = restrictToTwoDecimals(value);
        if (!checkIsNumber(restricted || 0)) return;
        handleQtyChange(restricted, quantitiesFieldpath(row.parentIndex, row.chindex).EXPAND_QUANTITY_PER_SIZCOL, row.colourwayId);
        if (get(quantities, `[${row.parentIndex}][${row.chindex}].quantityPerSizCol`, '')) {
          updateError(errorpath(row.parentIndex, row.chindex).QUANTITIES.ERROR_QUANTITY);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rowData, quantities],
  );

  const handleFillCells = (changes: BombiixFillBatchItem<QuantitiesGridRow>[]): void => {
    if (!Array.isArray(changes) || changes.length === 0) return;
    const copyState: DetailedQuantitiesState = cloneDeep(detailedQuanitiesState);
    const touchedColourwayIds = new Set<string>();

    changes.forEach((change) => {
      if (change.field !== 'quantity') return;
      const row = rowData.find((item) => item.__cagId === change.rowId);
      if (!row || row.__rowType !== 'data') return;

      const restricted = restrictToTwoDecimals(change.newValue);
      if (!checkIsNumber(restricted || 0)) return;

      set(copyState, quantitiesFieldpath(row.parentIndex, row.chindex).EXPAND_QUANTITY_PER_SIZCOL, restricted);
      if (row.colourwayId) touchedColourwayIds.add(row.colourwayId);
    });

    const sharedColourwayId = touchedColourwayIds.size === 1 ? [...touchedColourwayIds][0] : undefined;
    calculateQuantities(copyState, sharedColourwayId);
  };

  const columnDefs = useMemo<BombiixColumnDefOrGroup<QuantitiesGridRow>[]>(() => {
    const cols: BombiixColumnDefOrGroup<QuantitiesGridRow>[] = [];

    if (shouldShowExpand) {
      cols.push({
        colId: '__expand',
        headerName: '',
        width: 40,
        minWidth: 40,
        maxWidth: 40,
        sortable: false,
        resizable: false,
        suppressHeaderMenuButton: true,
        cellRenderer: HierExpandCell,
        cellProps: {
          isParent: (row: QuantitiesGridRow) => row.__showChevron,
          getExpanded: (row: QuantitiesGridRow) => row.isExpanded,
          onToggle: (row: QuantitiesGridRow) => expandQuantitiesChild(row.parentIndex ?? null),
        },
        headerComponent: ExpandAllHeader,
        headerComponentParams: { allExpanded: quantitesAllExpand, onToggleAll: (val: boolean) => expandQuantitiesChild(null, val) },
      });
    }

    if (hideColourCol) {
      cols.push({
        field: 'colourway',
        headerName: t('DEVLOPMENT_ORDER.EXPAND_VIEW.QUANTITIES_TABLE.COLOUR_WAY'),
        cellType: 'readonlyText',
        minWidth: 120,
        flex: 1,
        sortable: false,
        cellProps: { accessor: (row: QuantitiesGridRow) => row.colourway },
      });
    }

    if (showSizeCol) {
      cols.push({
        field: 'size',
        headerName: t('DEVLOPMENT_ORDER.EXPAND_VIEW.QUANTITIES_TABLE.SIZE'),
        cellType: 'readonlyText',
        minWidth: 120,
        flex: 1,
        sortable: false,
        cellProps: { accessor: (row: QuantitiesGridRow) => row.size },
      });
    }

    if (hideLengthCol) {
      cols.push({
        field: 'length',
        headerName: lengthDisplayLabel,
        cellType: 'readonlyText',
        minWidth: 120,
        flex: 1,
        sortable: false,
        cellProps: { accessor: (row: QuantitiesGridRow) => row.length },
      });
    }

    cols.push({
      field: 'quantity',
      headerName: t('DEVLOPMENT_ORDER.EXPAND_VIEW.QUANTITIES_TABLE.QUANTITY'),
      required: true,
      cellType: 'text',
      minWidth: 124,
      flex: 1,
      sortable: false,
      getCellReadOnly: (row: QuantitiesGridRow) => row.__rowType !== 'data' || Boolean(row.readonlyQty),
      getCellError: (row: QuantitiesGridRow) => row.__err,
      cellProps: { type: 'number', accessor: (row: QuantitiesGridRow) => (row.__rowType === 'data' ? roundOff(row.quantity, 2, true) : row.quantity) },
    });

    return cols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldShowExpand, showSizeCol, hideColourCol, hideLengthCol, quantitesAllExpand, t, lengthDisplayLabel]);

  return (
    <CostingCardShell title={t('DEVLOPMENT_ORDER.PRODUCT_COSTING.CORE_PRODUCT')}>
      {(fullView) => (
        <>
          <BombiixAGGrid<QuantitiesGridRow>
            hideCellErrorMessage
            revealHeaderControlsOnHover
            sortable={false}
            showColumnMenu={false}
            suppressColumnResize
            enableFillHandle
            onFillCells={handleFillCells}
            columnDefs={columnDefs}
            rowData={rowData}
            getRowId={(row) => row.__cagId}
            onRowChange={onRowChange}
            isRowReadOnly={(row) => row.__rowType !== 'data'}
            getRowClass={(row) => {
              if (row?.__rowType === 'grandTotal') return 'cag-hier-row--total';
              if (row?.__rowType === 'summary') return 'cag-hier-row--summary';
              if (row?.__rowType === 'data' && row?.isExpanded && !row?.__showChevron) return 'cag-hier-row--child';
              return '';
            }}
            {...(fullView ? {} : { height: CORE_COLLAPSED_HEIGHT })}
            autoSizeColumns
            gridOptions={{ rowHeight: 52, suppressMovableColumns: true }}
          />
          {(!Array.isArray(quantitiesInfo) || quantitiesInfo.length === 0) && <p className="text-center mt-2 mb-0">{t('COSTING_TO_DO_LIST.NO_RECORD_FOUND')}</p>}
        </>
      )}
    </CostingCardShell>
  );
}

export default QuantitiesTable;
